import { HCO_CUBE_NAME } from '@/api/hco/constants';
import { isNonEmptyString, isStringArray } from '@/api/queries/utils';
import { SESSION_STORAGE_SYNC_EVENT } from '@/hooks/useSessionStorage';
import { GetOptionsQueryFunction } from '@/types/cube';
import { FilterConfigDynamic } from '@/types/filter';
import { SelectItem } from '@/types/ui';
import { toStringOrNull } from '@/utils/cube';
import { sortBy } from 'lodash-es';
import { HCO_COUNTRY_FILTER_LIMIT } from '../../filterDefaultLimits';
import { extractCountryName, resolveCountryCodes } from '../../utils/countryFilterHelpers';
import expandHierarchicalPaths from '../../utils/expandHierarchicalPaths';

// Distinct from the HCP country filter's key so the two selections don't
// overwrite each other in session storage.
export const RESOLVED_HCO_COUNTRY_CODES_KEY = 'RESOLVED_HCO_COUNTRY_CODES';

function parseStoredCountryCodes(rawStoredCodes: string | null): string[] {
  if (!rawStoredCodes) return [];
  try {
    const parsedValue: unknown = JSON.parse(rawStoredCodes);
    if (!Array.isArray(parsedValue)) return [];
    return parsedValue.every(parsedCode => typeof parsedCode === 'string') ? parsedValue : [];
  } catch {
    return [];
  }
}

export const readCachedHcoCountryCodes = (): string[] =>
  parseStoredCountryCodes(sessionStorage.getItem(RESOLVED_HCO_COUNTRY_CODES_KEY));

export const writeCachedHcoCountryCodes = (codes: string[]): void => {
  if (codes.length === 0) {
    sessionStorage.removeItem(RESOLVED_HCO_COUNTRY_CODES_KEY);
  } else {
    sessionStorage.setItem(RESOLVED_HCO_COUNTRY_CODES_KEY, JSON.stringify(codes));
  }
  window.dispatchEvent(
    new CustomEvent(SESSION_STORAGE_SYNC_EVENT, { detail: { key: RESOLVED_HCO_COUNTRY_CODES_KEY } }),
  );
};

const REGION_AFFILIATE_COUNTRY_CUBE_NAME = 'region_affiliate_country';

const displayDimensions = [
  `${REGION_AFFILIATE_COUNTRY_CUBE_NAME}.roche_business_region`,
  `${REGION_AFFILIATE_COUNTRY_CUBE_NAME}.roche_affiliate_name`,
  `${REGION_AFFILIATE_COUNTRY_CUBE_NAME}.country_name`,
];

// Querying hco.country_code forces the join to the hco cube, so options only
// include countries with HCOs and apply-time can filter on native ISO codes.
const COUNTRY_CODE_DIMENSION = `${HCO_CUBE_NAME}.country_code`;
const COUNTRY_NAME_DIMENSION = `${REGION_AFFILIATE_COUNTRY_CUBE_NAME}.country_name`;

const HCO_COUNTRY_OPTIONS: GetOptionsQueryFunction = () => ({
  dimensions: [...displayDimensions, COUNTRY_CODE_DIMENSION],
  limit: HCO_COUNTRY_FILTER_LIMIT,
});

let cachedCountryOptions: SelectItem<string>[] = [];
let cachedCountries: SelectItem<string>[] = [];
const countryNameToCode = new Map<string, string>();

const hcoCountryFilter: FilterConfigDynamic = {
  label: 'global.filters.formControls.hcoCountryLabel',
  placeholder: 'global.filters.formControls.hcoCountryPlaceholder',
  type: 'oldtreeselect',
  defaultValue: [],
  clearable: true,
  searchable: true,
  collapseSingleChild: true,
  applyDynamicFilters: true,
  sourceCube: HCO_CUBE_NAME,
  getOptionsQuery: HCO_COUNTRY_OPTIONS,
  mapResult: countryNode => {
    const region = toStringOrNull(countryNode[displayDimensions[0]]);
    const affiliate = toStringOrNull(countryNode[displayDimensions[1]]);
    const country = toStringOrNull(countryNode[COUNTRY_NAME_DIMENSION]);
    const code = toStringOrNull(countryNode[COUNTRY_CODE_DIMENSION]);
    if (country && code) countryNameToCode.set(country, code);
    const result = [region, affiliate, country].filter(Boolean).join('__');
    return { label: result, value: result };
  },
  processOptions: options => {
    const expanded = expandHierarchicalPaths(options);
    cachedCountryOptions = expanded;
    cachedCountries = [...countryNameToCode].map(([name, code]) => ({ label: name, value: code }));
    return expanded;
  },
  sort: unsortedData => sortBy(unsortedData, 'label'),
  format: ({ value }) => {
    if (!isNonEmptyString(value)) return '';
    return extractCountryName(value);
  },
  applyFilter: value => {
    if (!isStringArray(value) || value.length === 0) {
      writeCachedHcoCountryCodes([]);
      return undefined;
    }

    const hasLiveCache = cachedCountryOptions.length > 0 && cachedCountries.length > 0;
    let countryCodes: string[];

    if (hasLiveCache) {
      countryCodes = [
        ...new Set(value.flatMap(path => resolveCountryCodes(path, cachedCountryOptions, cachedCountries))),
      ].sort();
      writeCachedHcoCountryCodes(countryCodes);
    } else {
      countryCodes = readCachedHcoCountryCodes();
    }

    if (countryCodes.length === 0) return undefined;

    return {
      member: COUNTRY_CODE_DIMENSION,
      operator: 'equals',
      values: countryCodes,
    };
  },
};

export default hcoCountryFilter;
